import { computed, ComputedRef, Ref } from "vue"
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/vue-query"
import {
  BulkChangePatientConversationAssignmentRequest,
  ChangePatientConversationAssignmentRequest,
  ChangePatientConversationStatusRequest,
  PatientConversationAttachmentDTO,
  CreatePatientConversationMessageRequest,
  CreatePatientConversationRequest,
  PatientConversationApiListPatientConversationsRequest,
  PatientConversationAssignmentResultDTO,
  PatientConversationConfigDTO,
  PatientConversationDTO,
  PatientConversationEligibilityDTO,
  PatientConversationInboxCountsDTO,
  PatientConversationMessageDTO,
  PatientConversationMessageListResponse,
  PatientConversationSummaryListResponse
} from "@vitable/asclepius-api-client-ts"
import { AsclepiusApiResponseError, useAsclepius } from "@/composables/api/asclepius"
import { useStore } from "@/store"
import { undefinedIfEmpty } from "@/utils/common"
import { InboxView } from "@/modules/patient-conversations/constants/patient-conversation-labels"

const CONFIG_STALE_TIME_MS = 5 * 60_000
const MESSAGES_PAGE_SIZE = 30
// No real-time channel (TDP §2.2): counts and the open thread poll at the cadence `inbox-counts` is sized for (§4.2.2)
const POLL_INTERVAL_MS = 60_000

type MaybeId = Ref<string | null | undefined> | ComputedRef<string | null | undefined>

export const patientConversationKeys = {
  all: ["patient-conversations"] as const,
  config: () => [...patientConversationKeys.all, "config"] as const,
  counts: () => [...patientConversationKeys.all, "inbox-counts"] as const,
  lists: () => [...patientConversationKeys.all, "list"] as const,
  list: (params: PatientConversationListParams) => [...patientConversationKeys.lists(), params] as const,
  details: () => [...patientConversationKeys.all, "detail"] as const,
  detail: (patientConversationId: string) => [...patientConversationKeys.details(), patientConversationId] as const,
  messages: (patientConversationId: string) =>
    [...patientConversationKeys.all, "messages", patientConversationId] as const,
  eligibility: (memberId: string) => [...patientConversationKeys.all, "eligibility", memberId] as const
}

/** The staff list filters, with `view` narrowed to the inbox views this app builds. */
export type PatientConversationListParams = Omit<
  PatientConversationApiListPatientConversationsRequest,
  "view" | "status" | "xVitableOrganization"
> & { view?: InboxView }

// A 4xx answer (e.g. `422 staff_not_allowed`, `404`) is final; only transport failures are worth retrying.
const retryTransportFailuresOnly = (failureCount: number, error: unknown): boolean =>
  !(error instanceof AsclepiusApiResponseError) && failureCount < 3

// Anything that changes a row (read, sent, assigned, status) moves the inbox rows and the badge counts together
function invalidateInbox(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: patientConversationKeys.lists() }).then()
  queryClient.invalidateQueries({ queryKey: patientConversationKeys.counts() }).then()
}

/** Business hours, SLA thresholds and whether the signed-in user may use patient messaging at all. */
export function usePatientConversationConfig() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const store = useStore()
  const isLoggedIn = computed<boolean>(() => !!store.getters["me/isLoggedIn"])

  async function getConfig(): Promise<PatientConversationConfigDTO> {
    const response = await callWithResponse(() => getPatientConversationApi().getPatientConversationConfig())
    return response.data
  }

  return useQuery({
    queryKey: patientConversationKeys.config(),
    queryFn: getConfig,
    enabled: isLoggedIn,
    staleTime: CONFIG_STALE_TIME_MS
  })
}

/**
 * Unread total for the side-menu badge, one count per inbox view, and the time of the newest message the viewer
 * can see. Polls every minute while the tab is visible and pauses when it is hidden.
 */
export function usePatientConversationInboxCounts() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const { data: config } = usePatientConversationConfig()

  async function getInboxCounts(): Promise<PatientConversationInboxCountsDTO> {
    const response = await callWithResponse(() => getPatientConversationApi().getPatientConversationInboxCounts())
    return response.data
  }

  return useQuery({
    queryKey: patientConversationKeys.counts(),
    queryFn: getInboxCounts,
    enabled: computed(() => config.value?.enabled === true),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: retryTransportFailuresOnly
  })
}

interface ListPatientConversationsOptions {
  /** Off for the inbox, which offers a Refresh bar instead of reordering rows under a selection. */
  refetchOnWindowFocus?: boolean
}

/** One page of the staff inbox (or one patient's history via `memberId`). The server owns the ordering. */
export function useListPatientConversations(
  params: Ref<PatientConversationListParams> | ComputedRef<PatientConversationListParams>,
  { refetchOnWindowFocus = true }: ListPatientConversationsOptions = {}
) {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()

  async function listConversations(): Promise<PatientConversationSummaryListResponse> {
    const { view, search, memberId, page, limit } = params.value
    return callWithResponse(() =>
      getPatientConversationApi().listPatientConversations({
        view,
        search: undefinedIfEmpty(search),
        memberId,
        page,
        limit
      })
    )
  }

  return useQuery({
    queryKey: computed(() => patientConversationKeys.list(params.value)),
    queryFn: listConversations,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus,
    retry: retryTransportFailuresOnly
  })
}

/**
 * One conversation's header, status and assignment history. Opening it is reading it: the server records the
 * viewer's read receipt on this call, so the inbox rows are refreshed to drop their unread state.
 */
export function usePatientConversation(patientConversationId: MaybeId) {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function getConversation(): Promise<PatientConversationDTO> {
    const id = patientConversationId.value
    if (!id) throw new Error("No conversation to load")
    const response = await callWithResponse(() =>
      getPatientConversationApi().getPatientConversation({ patientConversationId: id })
    )
    invalidateInbox(queryClient)
    return response.data
  }

  return useQuery({
    queryKey: computed(() => patientConversationKeys.detail(patientConversationId.value ?? "")),
    queryFn: getConversation,
    enabled: computed(() => !!patientConversationId.value),
    retry: retryTransportFailuresOnly
  })
}

/** The thread, newest page first; `fetchNextPage` loads older messages. */
export function usePatientConversationMessages(patientConversationId: MaybeId) {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()

  async function listMessages({ pageParam }: { pageParam: number }): Promise<PatientConversationMessageListResponse> {
    const id = patientConversationId.value
    if (!id) throw new Error("No conversation to load")
    return callWithResponse(() =>
      getPatientConversationApi().listPatientConversationMessages({
        patientConversationId: id,
        page: pageParam,
        limit: MESSAGES_PAGE_SIZE
      })
    )
  }

  return useInfiniteQuery({
    queryKey: computed(() => patientConversationKeys.messages(patientConversationId.value ?? "")),
    queryFn: listMessages,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.total_pages ? lastPage.pagination.page + 1 : undefined,
    enabled: computed(() => !!patientConversationId.value),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: retryTransportFailuresOnly
  })
}

/**
 * The thread polls every minute; this tells the provider when the messages were last fetched and lets them pull
 * the thread and header again on demand. Shares the thread query, so sending a reply or a poll also moves the time.
 */
export function useRefreshPatientConversation(patientConversationId: MaybeId) {
  const queryClient = useQueryClient()
  const { dataUpdatedAt, isFetching, refetch } = usePatientConversationMessages(patientConversationId)

  async function refresh(): Promise<void> {
    const id = patientConversationId.value
    if (!id) return
    await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: patientConversationKeys.detail(id) })])
  }

  return { lastRefreshedAt: dataUpdatedAt, isRefreshing: isFetching, refresh }
}

/** Whether "Message patient" may start a conversation with this member (`mbr_…`), and why not. */
export function usePatientConversationEligibility(memberId: MaybeId) {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()

  async function getEligibility(): Promise<PatientConversationEligibilityDTO> {
    const id = memberId.value
    if (!id) throw new Error("No member to check")
    const response = await callWithResponse(() =>
      getPatientConversationApi().getPatientConversationEligibility({ memberId: id })
    )
    return response.data
  }

  return useQuery({
    queryKey: computed(() => patientConversationKeys.eligibility(memberId.value ?? "")),
    queryFn: getEligibility,
    enabled: computed(() => !!memberId.value),
    retry: retryTransportFailuresOnly
  })
}

export interface SendPatientConversationMessageVariables {
  patientConversationId: string
  request: CreatePatientConversationMessageRequest
}

/** Posts a message to an existing conversation; the thread, header and inbox rows refresh afterwards. */
export function useSendPatientConversationMessage() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function sendMessage({
    patientConversationId,
    request
  }: SendPatientConversationMessageVariables): Promise<PatientConversationMessageDTO> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().createPatientConversationMessage({
        patientConversationId,
        createPatientConversationMessageRequest: request
      })
    )
    return response.data
  }

  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (_message, { patientConversationId }) => {
      queryClient.invalidateQueries({ queryKey: patientConversationKeys.messages(patientConversationId) }).then()
      queryClient.invalidateQueries({ queryKey: patientConversationKeys.detail(patientConversationId) }).then()
      invalidateInbox(queryClient)
    }
  })
}

/** Starts a conversation with a member (opening message included); the inbox rows refresh afterwards. */
export function useStartPatientConversation() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function startConversation(request: CreatePatientConversationRequest): Promise<PatientConversationDTO> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().createPatientConversation({ createPatientConversationRequest: request })
    )
    return response.data
  }

  return useMutation({
    mutationFn: startConversation,
    onSuccess: () => {
      invalidateInbox(queryClient)
    }
  })
}

export interface ChangePatientConversationAssignmentVariables {
  patientConversationId: string
  request: ChangePatientConversationAssignmentRequest
}

/** Assigns, reassigns or unassigns one conversation (`assignee_id: null` unassigns). */
export function useChangePatientConversationAssignment() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function changeAssignment({
    patientConversationId,
    request
  }: ChangePatientConversationAssignmentVariables): Promise<PatientConversationDTO> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().changePatientConversationAssignment({
        patientConversationId,
        changePatientConversationAssignmentRequest: request
      })
    )
    return response.data
  }

  return useMutation({
    mutationFn: changeAssignment,
    onSuccess: (conversation) => {
      queryClient.setQueryData(patientConversationKeys.detail(conversation.id), conversation)
      invalidateInbox(queryClient)
    }
  })
}

export interface ChangePatientConversationStatusVariables {
  patientConversationId: string
  request: ChangePatientConversationStatusRequest
}

/** Archives (`Archived`), unarchives (`Open`) or closes (`Closed`) a conversation; closing adds a notice to the thread. */
export function useChangePatientConversationStatus() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function changeStatus({
    patientConversationId,
    request
  }: ChangePatientConversationStatusVariables): Promise<PatientConversationDTO> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().changePatientConversationStatus({
        patientConversationId,
        changePatientConversationStatusRequest: request
      })
    )
    return response.data
  }

  return useMutation({
    mutationFn: changeStatus,
    onSuccess: (conversation) => {
      queryClient.setQueryData(patientConversationKeys.detail(conversation.id), conversation)
      queryClient.invalidateQueries({ queryKey: patientConversationKeys.messages(conversation.id) }).then()
      invalidateInbox(queryClient)
    }
  })
}

export interface UploadPatientConversationAttachmentVariables {
  patientConversationId: string
  file: File
}

/** Uploads one file as a pending attachment; its `id` goes in the message's `attachment_ids`. */
export function useUploadPatientConversationAttachment() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()

  async function uploadAttachment({
    patientConversationId,
    file
  }: UploadPatientConversationAttachmentVariables): Promise<PatientConversationAttachmentDTO> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().uploadPatientConversationAttachment({
        patientConversationId,
        // The generated client types multipart files as `string`; axios sends the File as form data
        file: file as unknown as string
      })
    )
    return response.data
  }

  return useMutation({ mutationFn: uploadAttachment })
}

/** Assigns or unassigns many conversations at once; the result carries one outcome per conversation. */
export function useBulkChangePatientConversationAssignment() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()
  const queryClient = useQueryClient()

  async function bulkChangeAssignment(
    request: BulkChangePatientConversationAssignmentRequest
  ): Promise<Array<PatientConversationAssignmentResultDTO>> {
    const response = await callWithResponse(() =>
      getPatientConversationApi().bulkChangePatientConversationAssignment({
        bulkChangePatientConversationAssignmentRequest: request
      })
    )
    return response.data
  }

  return useMutation({
    mutationFn: bulkChangeAssignment,
    // Not awaited: the mutation should settle as soon as the server answers, not after the lists refetch.
    onSettled: () => {
      invalidateInbox(queryClient)
      queryClient.invalidateQueries({ queryKey: patientConversationKeys.details() }).then()
    }
  })
}
